import React from 'react';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import axios from '../axiosClient.js';
import Alert from './common/Alert.jsx';
import PageLoading from './common/PageLoading.jsx';
import MonthlyCommissionsHeader from './MonthlyCommissionsHeader.jsx';
import MonthlyCommissionsRow from './MonthlyCommissionsRow.jsx';

const REFRESH_INTERVAL_MS = 300_000;

function MonthlyCommissions({ selectedRep, selectedYear, enabled = true }) {
  const [alertError, setAlertError] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [monthlyCommissions, setMonthlyCommissions] = React.useState([]);

  // Fetch explicitly on enable/selection; refresh only after the last request settles.
  React.useEffect(() => {
    const controller = new AbortController();
    let timer;
    setLoading(true);
    setAlertError(null);
    setMonthlyCommissions([]);

    const fetchSummary = async () => {
      try {
        const { data } = await axios.get(
          `/api/monthly-commissions/${encodeURIComponent(selectedRep)}/${selectedYear}`,
          { signal: controller.signal, timeout: 60000 }
        );
        if (controller.signal.aborted) return;
        setMonthlyCommissions(data);
        setAlertError(null);
      } catch (err) {
        if (controller.signal.aborted) return;
        setAlertError(err.response?.data?.error || err.message);
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
          timer = setTimeout(fetchSummary, REFRESH_INTERVAL_MS);
        }
      }
    };

    if (enabled && selectedRep && selectedYear) fetchSummary();
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [selectedRep, selectedYear, enabled]);

  return (
    <Box>
      <Typography data-cy="monthly-commissions-header" variant="h5" sx={{ textAlign: 'center' }}>
        Monthly Commissions
      </Typography>
      <Alert
        data-cy="monthly-commissions-alert"
        message={alertError}
        setMessage={setAlertError}
        level="error"
      />
      {loading ? (
        <PageLoading />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <MonthlyCommissionsHeader />
            </TableHead>
            <TableBody>
              {monthlyCommissions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={20} align="center">
                    No category groups configured for this rep and year.
                  </TableCell>
                </TableRow>
              )}
              {monthlyCommissions.map((monthCommission) => (
                <MonthlyCommissionsRow
                  key={monthCommission.category}
                  monthCommission={monthCommission}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

export default React.memo(MonthlyCommissions);
